import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Terminal, Copy, Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface CommandCopyBoxProps {
  /** Label rendered next to the Terminal icon. */
  label: string;
  /** The command string displayed in the monospace box. */
  command: string;
  /** Caption shown below the command box. */
  helperText: string;
  /** data-help-id applied to the <code> element. */
  commandHelpId?: string;
  /** data-help-id applied to the copy button. */
  copyButtonHelpId?: string;
  /** Extra classes on the outer container. */
  className?: string;
}

export function CommandCopyBox({
  label,
  command,
  helperText,
  commandHelpId,
  copyButtonHelpId,
  className,
}: CommandCopyBoxProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = useCallback(async () => {
    let success = false;
    try {
      await navigator.clipboard.writeText(command);
      success = true;
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = command;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      try {
        textArea.select();
        if (typeof document.execCommand === "function") {
          // eslint-disable-next-line @typescript-eslint/no-deprecated -- fallback for older browsers
          success = document.execCommand("copy");
        }
      } finally {
        document.body.removeChild(textArea);
      }
    }
    if (!success) return;
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setCopied(false);
      timerRef.current = null;
    }, 2000);
  }, [command]);

  return (
    <div className={cn(className)}>
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2">
        <Terminal className="h-4 w-4" />
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <code
          className="flex-1 rounded-lg bg-white/[0.03] border border-white/[0.06] px-3 py-2 text-sm font-mono select-all overflow-x-auto"
          {...(commandHelpId ? { "data-help-id": commandHelpId } : {})}
        >
          {command}
        </code>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          aria-label={copied ? "Copied command" : "Copy command"}
          onClick={() => { void handleCopy(); }}
          {...(copyButtonHelpId ? { "data-help-id": copyButtonHelpId } : {})}
        >
          {copied ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {helperText}
      </p>
    </div>
  );
}
